import api from "../api";
import type { User } from "../auth";
import { SearchParams } from "./communityService";

// Event interface matching Prisma schema
export interface Event {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  allDay: boolean;
  image?: string;
  locationName?: string;
  city?: string;
  country?: string;
  creatorId: string;
  creator: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  };
  createdAt: string;
  updatedAt: string;
  attendeeCount: number;
  userRsvpStatus: "going" | "interested" | null;
}

// Event creation data interface
export interface CreateEventData {
  title: string;
  description: string;
  startDate: string; // ISO string
  endDate?: string; // ISO string
  allDay?: boolean;
  locationName?: string;
  city?: string;
  country?: string;
  category?: string;
  image?: File; // For image upload
  tagIds?: string[]; // For tagging the event
}

// Event service for managing music events and shows
const eventService = {
  // Get all events with pagination and filters
  getEvents: async (
    params: {
      page?: number;
      limit?: number;
      upcoming?: boolean;
      category?: string;
      search?: string;
    } = {},
  ): Promise<{ events: Event[]; total: number }> => {
    const response = await api.get<{
      success: boolean;
      data: Event[];
      pagination: { totalCount: number };
    }>("events", { params });

    return {
      events: response.data.data,
      total: response.data.pagination.totalCount,
    };
  },

  // Get event by ID
  getEvent: async (eventId: string): Promise<Event> => {
    const response = await api.get<{ success: boolean; data: Event }>(
      `events/${eventId}`,
    );
    return response.data.data;
  },

  // Create new event
  createEvent: async (data: CreateEventData): Promise<Event> => {
    // Handle file upload using FormData if there's an image
    const formData = new FormData();

    // Add image file if it exists
    if (data.image instanceof File) {
      formData.append("image", data.image);
    }

    // Add other data as JSON string
    const eventData = { ...data };
    delete eventData.image; // Remove the image from the data object
    formData.append("data", JSON.stringify(eventData));

    const response = await api.post<{ success: boolean; event: Event }>(
      "events",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return response.data.event;
  },

  // Update event
  updateEvent: async (
    eventId: string,
    data: Partial<CreateEventData>,
  ): Promise<Event> => {
    const response = await api.put<{ event: Event }>(`events/${eventId}`, data);
    return response.data.event;
  },

  // Delete event
  deleteEvent: async (eventId: string): Promise<void> => {
    await api.delete(`events/${eventId}`);
  },

  // Update event status
  updateEventStatus: async (
    eventId: string,
    status: string,
  ): Promise<Event> => {
    const response = await api.patch<{ event: Event }>(
      `events/${eventId}/status`,
      { status },
    );
    return response.data.event;
  },

  // RSVP going to event
  rsvpGoing: async (
    eventId: string,
  ): Promise<{ attendeeCount: number; userRsvpStatus: string }> => {
    const response = await api.post<{
      attendeeCount: number;
      userRsvpStatus: string;
    }>(`events/${eventId}/rsvp`, { status: "going" });
    return response.data;
  },

  // RSVP interested in event
  rsvpInterested: async (
    eventId: string,
  ): Promise<{ attendeeCount: number; userRsvpStatus: string }> => {
    const response = await api.post<{
      attendeeCount: number;
      userRsvpStatus: string;
    }>(`events/${eventId}/rsvp`, { status: "interested" });
    return response.data;
  },

  // Cancel RSVP
  cancelRsvp: async (
    eventId: string,
  ): Promise<{ attendeeCount: number; userRsvpStatus: null }> => {
    const response = await api.delete<{
      attendeeCount: number;
      userRsvpStatus: null;
    }>(`events/${eventId}/rsvp`);
    return response.data;
  },

  // Get event attendees
  getEventAttendees: async (
    eventId: string,
    params: {
      page?: number;
      limit?: number;
      status?: "going" | "interested" | "all";
    } = {},
  ): Promise<{ users: User[]; total: number }> => {
    const response = await api.get<{ users: User[]; total: number }>(
      `events/${eventId}/attendees`,
      { params },
    );
    return response.data;
  },

  // Get events for a specific user
  getUserEvents: async (
    userId: string,
    params: {
      page?: number;
      limit?: number;
      type?: "organizing" | "attending" | "interested";
    } = {},
  ): Promise<{ events: Event[]; total: number }> => {
    const response = await api.get<{ events: Event[]; total: number }>(
      `users/${userId}/events`,
      { params },
    );
    return response.data;
  },

  // Upload event image
  uploadEventImage: async (eventId: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("image", file);

    const response = await api.post<{ imageUrl: string }>(
      `events/${eventId}/image`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return response.data.imageUrl;
  },

  // Delete event image
  deleteEventImage: async (eventId: string): Promise<void> => {
    await api.delete(`events/${eventId}/image`);
  },

  // Search events
  searchEvents: async ({
    query,
    page = 1,
    limit = 10,
  }: SearchParams): Promise<{ events: Event[]; total: number }> => {
    return eventService.getEvents({ search: query, page, limit });
  },
};

export default eventService;
